/* Optional cache warming only. Native loading, navigation and Forms stay in control. */
(() => {
  if (window.Shopify?.designMode) return;
  const main = document.querySelector('main');
  if (!main) return;
  const connection = navigator.connection;
  const attemptedImages = new WeakSet();
  const visitedPages = new Set();
  const pendingPages = [];
  let imageCount = 0;
  let readyAt = 0;
  let timer;
  let hoverTimer;
  let busy = false;
  let stopped = false;
  let idleCollectionQueued = false;
  let controller;

  const constrained = () => connection?.saveData ||
    /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '') ||
    (connection?.downlink > 0 && connection.downlink < 1.5);
  const paused = () => stopped || document.hidden || navigator.onLine === false || constrained() ||
    !!document.querySelector('[data-inquiry-drawer][open]') ||
    !!document.activeElement?.closest('form, [data-official-inquiry]');
  const firstViewReady = () => {
    const intro = document.querySelector('[data-page-loader]');
    if (intro && !intro.hidden) return false;
    return [...main.querySelectorAll('img[fetchpriority="high"], img[loading="eager"]')]
      .filter(img => {const r = img.getBoundingClientRect(); return r.top < innerHeight && r.bottom > 0;})
      .every(img => img.complete);
  };
  const schedule = () => { clearTimeout(timer); if (!stopped) timer = setTimeout(tick, 1000); };
  const waitForImage = img => new Promise(resolve => {
    if (img.complete && img.currentSrc) return resolve();
    const done = () => {img.removeEventListener('load', done); img.removeEventListener('error', done); resolve();};
    img.addEventListener('load', done, {once:true});
    img.addEventListener('error', done, {once:true});
  });
  const nextImage = () => {
    if (imageCount >= 32) return null;
    return [...main.querySelectorAll('img[loading="lazy"]')].filter(img => {
      if (attemptedImages.has(img) || (img.complete && img.naturalWidth) ||
          img.closest('[hidden], [aria-hidden="true"], [data-product-gallery-thumbnails], .collection-card__detail, .supplied-products__hover')) return false;
      const r = img.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && getComputedStyle(img).visibility !== 'hidden';
    }).sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
  };
  const safePage = anchor => {
    if (!anchor || anchor.hasAttribute('download') || anchor.target ||
        anchor.closest('[data-inquiry-add], [data-inquiry-open], [data-quick-contact-toggle]')) return null;
    try {
      const url = new URL(anchor.href, location.href);
      if (url.origin !== location.origin || url.search || url.hash || url.pathname === location.pathname) return null;
      // Locale prefixes are allowed; filters, account/cart/actions and file endpoints are not.
      if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:products\/[^/.]+|collections\/[^/.]+(?:\/products\/[^/.]+)?)\/?$/i.test(url.pathname)) return null;
      return url.href;
    } catch { return null; }
  };
  const enqueue = anchor => {
    if (paused()) return;
    const url = safePage(anchor);
    if (!url || visitedPages.has(url) || visitedPages.size >= 3) return;
    visitedPages.add(url);
    pendingPages.push(url);
  };
  const imageUrl = (value, base) => {
    if (!value) return null;
    try {
      const url = new URL(value, base);
      return url.origin === location.origin || (url.protocol === 'https:' && url.hostname === 'cdn.shopify.com') ? url.href : null;
    } catch { return null; }
  };
  async function warmPage(url) {
    controller = new AbortController();
    const timeout = setTimeout(() => controller?.abort(), 8000);
    try {
      const response = await fetch(url, {credentials:'same-origin', cache:'force-cache',
        priority:'low', redirect:'error', headers:{Accept:'text/html'}, signal:controller.signal});
      if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) return;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let html = '';
      let bytes = 0;
      while (true) {
        const {done, value} = await reader.read();
        if (done) break;
        bytes += value.byteLength;
        if (bytes > 512 * 1024) { await reader.cancel(); return; }
        html += decoder.decode(value, {stream:true});
      }
      html += decoder.decode();
      clearTimeout(timeout);
      if (paused()) return;
      // Template contents are inert: no scripts, app embeds or other images execute/load.
      const template = document.createElement('template');
      template.innerHTML = html;
      const content = template.content;
      const first = content.querySelector('[data-gallery-slide]:not([hidden]) img') ||
        content.querySelector('[data-visual-hero] img') || content.querySelector('main img');
      const src = imageUrl(first?.getAttribute('src'), url);
      if (!src) return;
      const img = new Image();
      img.fetchPriority = 'low';
      img.decoding = 'async';
      img.sizes = first.getAttribute('sizes') || '100vw';
      const candidates = (first.getAttribute('srcset') || '').split(',').map(item => {
        const [path, descriptor] = item.trim().split(/\s+/);
        const candidate = imageUrl(path, url);
        return candidate && /^\d+(?:\.\d+)?[wx]$/.test(descriptor || '') ? `${candidate} ${descriptor}` : '';
      }).filter(Boolean);
      if (candidates.length) img.srcset = candidates.join(', ');
      img.src = src;
      await waitForImage(img);
    } catch { /* Speculation must never affect real browsing. */ }
    finally { clearTimeout(timeout); controller = null; }
  }
  async function tick() {
    if (busy) return;
    if (paused() || !firstViewReady()) {schedule(); return;}
    if (!readyAt) {readyAt = performance.now(); schedule(); return;}
    busy = true;
    try {
      if (!idleCollectionQueued && performance.now() - readyAt >= 6000 && document.body.classList.contains('template-index')) {
        idleCollectionQueued = true;
        const link = [...document.querySelectorAll('header a[href]')].find(a => safePage(a)?.includes('/collections/'));
        enqueue(link);
      }
      if (pendingPages.length) await warmPage(pendingPages.shift());
      else {
        const img = nextImage();
        if (img) {
          attemptedImages.add(img);
          imageCount += 1;
          img.fetchPriority = 'low';
          const complete = waitForImage(img);
          // Leave src/srcset/sizes intact: browser chooses one appropriate candidate.
          img.loading = 'eager';
          await complete;
        }
      }
    } finally {busy = false; schedule();}
  }
  const anchorFor = event => event.target.closest?.('a[href]');
  document.addEventListener('pointerover', event => {
    if (event.pointerType === 'touch') return;
    const anchor = anchorFor(event);
    if (!anchor || anchor.contains(event.relatedTarget)) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => enqueue(anchor), 180);
  }, {passive:true});
  document.addEventListener('pointerout', event => {
    const anchor = anchorFor(event);
    if (anchor && !anchor.contains(event.relatedTarget)) clearTimeout(hoverTimer);
  }, {passive:true});
  document.addEventListener('focusin', event => enqueue(anchorFor(event)));
  document.addEventListener('touchstart', event => enqueue(anchorFor(event)), {passive:true});
  document.addEventListener('collection:updated', schedule);
  document.addEventListener('visibilitychange', () => {if (document.hidden) controller?.abort(); else schedule();});
  window.addEventListener('offline', () => controller?.abort());
  window.addEventListener('online', schedule);
  connection?.addEventListener?.('change', () => {if (constrained()) controller?.abort(); schedule();});
  window.addEventListener('pagehide', () => {stopped = true; clearTimeout(timer); clearTimeout(hoverTimer); controller?.abort();});
  window.addEventListener('pageshow', event => {if (event.persisted) {stopped = false; schedule();}});
  schedule();
})();
